import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { EntityTypeEnum, OrganisationType, WorkflowActions, EntityType } from 'api'
import { EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { createWorkflowFatFingerUserLimitDetailsSelector } from 'selectors/workflowSelectors'
import { AgGridWrapper, filteredColumnDefsBuilder } from 'view/components/datagrid'
import { Locale, Translation } from 'locale'
import { useModal } from 'view/shared/modal'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { GridHeader, Wrapper } from './FatFingerLimitsStyled'
import { fatFingerLimitsGridOptions, TradingFatFingerGridColumnId, tradingFatFingerLimitsColumnDefs } from './FatFingerLimitsGridModel'
import { FatFingerLimitDialog } from '../FatFingerLimitDialog'
import { createSortedEntityIdsSelector } from 'selectors/entitySelectors'
import { fatFingerLimitUserGetterSelector } from 'selectors/userSelectors'
import { useParametricSelector } from 'utils'

interface FatFingerUserLimitPanelProps {
  workflowId: string
  organisationType: OrganisationType
  organisationId: string
  readOnly: boolean
  userId: string
}

const workflowIdSelector = (_state: {}, props: FatFingerUserLimitPanelProps) => props.workflowId
const workflowFatFingerLimitDetailsSelector = createWorkflowFatFingerUserLimitDetailsSelector(workflowIdSelector)

export const FatFingerUserLimitPanel: React.FC<FatFingerUserLimitPanelProps> = props => {
  const { workflowId, readOnly, organisationId, userId } = props

  const dispatch = useDispatch()
  const { showModal } = useModal()
  const sectorIds = useSelector(createSortedEntityIdsSelector(EntityType.FAT_FINGER_PRODUCT_SECTOR))
  const userFatFingerLimits = useSelector(fatFingerLimitUserGetterSelector)(userId)
  const fatFingerLimitDetails = useParametricSelector(workflowFatFingerLimitDetailsSelector, props)

  const existingFatFingerUserLimitIds = fatFingerLimitDetails.map(limit => limit.fatFingerUserLimit.productSectorId)
  const handleNewFatFingerUserLimitRelationship = () => {
    const correlatedResponseCallback = (_workflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
      return showModal(
        <FatFingerLimitDialog
          organisationId={organisationId}
          workflowId={_workflowId}
          entityId={newlyAddedEntityIdAndTypes[EntityTypeEnum.FAT_FINGER_USER]}
          existingFatFingerLimitIds={existingFatFingerUserLimitIds}
          userId={userId}
        />
      )
    }
    dispatch(WorkflowActions.createAddToExistingWorkflow(workflowId, EntityTypeEnum.FAT_FINGER_USER, correlatedResponseCallback))
  }

  const noFatFingerUserLimitPlaceholder = () => <div>{Locale.translate(Translation.SC_NO_FAT_FINGER_LIMITS)}</div>
  const disabledAdd = () => {
    return sectorIds.length === existingFatFingerUserLimitIds?.length
  }

  const filteredColumnDefs = useMemo(() => {
    const columnDefs = tradingFatFingerLimitsColumnDefs()
    return filteredColumnDefsBuilder(columnDefs)
      .withoutIf(readOnly, [TradingFatFingerGridColumnId.FAT_FINGER_ACTIONS])
      .build()
  }, [readOnly])

  return (
    <Wrapper>
      <GridHeader>
        <TextButton
          type={TextButtonType.Primary}
          onClick={handleNewFatFingerUserLimitRelationship}
          disabled={readOnly || disabledAdd()}
          dataTestId={'FatFingerDialog-TextInputField-SaveButton'}>
          {Locale.translate(Translation.TC_ADD_FAT_FINGER_LIMITS)}
        </TextButton>
      </GridHeader>
      <AgGridWrapper
        dataTestId={'FatFingerLimitsGrid'}
        className="datagrid-borderless"
        gridOptions={fatFingerLimitsGridOptions}
        columnDefs={filteredColumnDefs}
        rowData={readOnly ? userFatFingerLimits : fatFingerLimitDetails}
        placeholder={noFatFingerUserLimitPlaceholder}
        context={{
          workflowId,
          organisationId,
          existingFatFingerUserLimitIds
        }}
      />
    </Wrapper>
  )
}
